'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { EmblaCarouselType } from 'embla-carousel-react';
import useEmblaCarousel, { type EmblaOptionsType } from 'embla-carousel-react';
import type { RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp, LuDot, LuMoveLeft, LuMoveRight } from 'react-icons/lu';

type CarouselClassName = {
  wrapper?: string;
  container?: string;
  slide?: string;
  activeSlide?: string;
  inactiveSlide?: string;
  arrows?: string;
  left_arrow?: string;
  right_arrow?: string;
  dots?: string;
  dot?: string;
  customDots?: string;
  customDot?: string;
  main?: string;
  thumbnail?: string;
  thumbnails?: string;
  thumbnailsWrapper?: string;
};

type CarouselCustomDots =
  | {
      showCustomDots: boolean;
      customDots: React.ReactNode[];
      colors: {
        activeColor: string;
        inactiveColor: string;
      };
    }
  | undefined;

export type CarouselProps = {
  className?: CarouselClassName;
  slides: React.ReactNode[];
  options?: EmblaOptionsType;
  customDots?: CarouselCustomDots;
  showArrows?: boolean;
  customArrowIcons?: { left?: React.ReactNode; right?: React.ReactNode };
  showDots?: boolean;
  showThumbnails?: boolean;
  showThumbnailsArrows?: boolean;
  enableInterval?: boolean;
  intervalLength?: number;
  enableLoop?: boolean;
  openLightbox?: () => void;
  showMoreButton?: boolean;
  allImagesCount?: number;
  emblaRef?: RefObject<EmblaCarouselType>;
};

export function Carousel(props: CarouselProps) {
  // Explicitly set loop option, ensuring it’s not overridden
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: props.enableLoop ?? false,
    ...props.options,
  });
  const thumbsRef = useRef<HTMLDivElement | null>(null);
  const dotsRef = useRef<HTMLDivElement | null>(null);
  const autoPlayIntervalRef = useRef<any>(null);

  // Imperative handle of emblaApi
  useImperativeHandle(props.emblaRef, () => emblaApi!, [emblaApi]);

  const carouselProviderContext = useCarouselContext();
  const isContextDefined = !!carouselProviderContext;

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    } else {
      console.warn('Embla API not available for prev');
    }
  }, [emblaApi]);

  const onNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    } else {
      console.warn('Embla API not available for next');
    }
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const startAutoPlay = () => {
      stopAutoPlay();
      autoPlayIntervalRef.current = setInterval(() => {
        if (emblaApi) {
          emblaApi.scrollNext();
        }
      }, props.intervalLength || 5000);
    };

    const stopAutoPlay = () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
    };

    if (props.enableInterval) {
      startAutoPlay();
    }

    return () => {
      stopAutoPlay();
    };
  }, [
    emblaApi,
    props.enableInterval,
    props.intervalLength,
    props.enableLoop,
    props.options,
    props.slides.length,
  ]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const newIndex = emblaApi.selectedScrollSnap();
      setSelectedIndex(newIndex);
      if (isContextDefined) {
        carouselProviderContext.setSelectedIndex(newIndex);
        // Allow navigation if looping is enabled
        const canGoNext = props.enableLoop ? true : emblaApi.canScrollNext();
        const canGoPrev = props.enableLoop ? true : emblaApi.canScrollPrev();
        carouselProviderContext.setCanGoNext(canGoNext);
        carouselProviderContext.setCanGoPrev(canGoPrev);
        ('Index changed:', {
          selectedIndex: newIndex,
          canGoNext,
          canGoPrev,
          slideCount: props.slides.length,
        });
      }
    };

    if (isContextDefined) {
      carouselProviderContext.setApi(emblaApi);
    }
    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
      if (isContextDefined) {
        carouselProviderContext.setApi(null);
      }
    };
  }, [emblaApi, isContextDefined, props.enableLoop, props.slides.length]);

  useEffect(() => {
    if (isContextDefined) {
      carouselProviderContext.setSlideCount(props.slides.length);
    }
  }, [isContextDefined, props.slides.length]);

  const scrollThumbs = useCallback(
    (delta: number) => {
      if (thumbsRef.current) {
        const container = thumbsRef.current;
        const currentScrollTop = container.scrollTop;
        container.scrollTo({
          top:
            delta < 0 && props.enableLoop && selectedIndex === 0
              ? container.scrollHeight
              : delta > 0 && props.enableLoop && selectedIndex === props.slides.length - 1
              ? 0
              : currentScrollTop + delta,
          behavior: 'smooth',
        });
      }
    },
    [props.enableLoop, selectedIndex, props.slides.length],
  );

  const scrollDots = useCallback(
    (delta: number) => {
      if (dotsRef.current) {
        const container = dotsRef.current;
        const currentScrollLeft = container.scrollLeft;
        container.scrollTo({
          left:
            delta < 0 && props.enableLoop && selectedIndex === 0
              ? container.scrollWidth
              : delta > 0 && props.enableLoop && selectedIndex === props.slides.length - 1
              ? 0
              : currentScrollLeft + delta,
          behavior: 'smooth',
        });
      }
    },
    [props.enableLoop, selectedIndex, props.slides.length],
  );

  const slides = props.slides.map((slide, index) => (
    <div
      className={cn(
        'h-full w-full flex-shrink-0 flex-grow-0',
        'ea-carousel__slide',
        selectedIndex === index ? props.className?.activeSlide : props.className?.inactiveSlide,
        props.className?.slide,
      )}
      key={index}
    >
      {slide}
    </div>
  ));

  const handleThumbClick = useCallback(
    (index: number) => {
      const indexDelta = index - selectedIndex;
      if (emblaApi) {
        emblaApi.scrollTo(index);
        scrollDots(indexDelta * 32);
        ('Thumbnail clicked, scrolled to index:', index);
      }
    },
    [emblaApi, selectedIndex, scrollDots],
  );

  const thumbs = props.slides.map((slide, index) => (
    <div
      className={cn(
        'min-h-[48px] w-12 cursor-pointer rounded-sm bg-muted',
        'ea-carousel__thumb',
        index === selectedIndex ? 'border border-foreground' : '',
        props.className?.thumbnail,
      )}
      key={index}
      onClick={() => handleThumbClick(index)}
    >
      {slide}
    </div>
  ));

  return (
    <div className={cn('flex w-full gap-1', 'ea-carousel__wrapper', props.className?.wrapper)}>
      {props.showThumbnails && (
        <div
          className={cn(
            'hidden h-full max-h-[500px] min-w-[56px] max-w-[56px] flex-col gap-2 md:flex',
            'ea-carousel__thumbnails',
            props.className?.thumbnailsWrapper,
          )}
        >
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-up',
              )}
              variant="secondary"
              disabled={!props.enableLoop && selectedIndex === 0}
              onClick={() => {
                onPrev();
                scrollThumbs(-56);
                scrollDots(-32);
              }}
            >
              <LuChevronUp size={16} />
            </Button>
          )}
          <div
            ref={thumbsRef}
            className={cn(
              'hide-scroll-bar flex min-w-[56px] max-w-[56px] flex-col items-center justify-start gap-2 overflow-hidden rounded-sm',
              'ea-carousel__thumbnail',
              props.className?.thumbnails,
            )}
          >
            {thumbs}
            {props.showMoreButton && (
              <div
                className={cn(
                  'flex min-h-[48px] w-12 cursor-pointer items-center justify-center rounded-lg bg-muted',
                  'ea-carousel__thumb',
                  props.className?.thumbnail,
                  'bg-primary text-background',
                )}
                onClick={() => props.openLightbox?.()}
              >
                {props.allImagesCount ? `+${props.allImagesCount - 4}` : 'Zobraziť viac'}
              </div>
            )}
          </div>
          {props.showThumbnailsArrows && (
            <Button
              className={cn(
                'h-5 rounded-sm',
                'ea-carousel__thumbnail-control ea-carousel__thumbnail-control-down',
              )}
              variant="secondary"
              disabled={!props.enableLoop && selectedIndex === props.slides.length - 1}
              onClick={() => {
                onNext();
                scrollThumbs(56);
                scrollDots(32);
              }}
            >
              <LuChevronDown size={16} />
            </Button>
          )}
        </div>
      )}
      <div
        className={cn('relative overflow-hidden', 'ea-carousel', props.className?.main)}
        ref={emblaRef}
      >
        <div
          className={cn('flex h-full', 'ea-carousel__container', props.className?.container)}
          onClick={props.openLightbox}
        >
          {slides}
        </div>
        {props.showArrows && (
          <>
            <button
              className={cn(
                'absolute left-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--left',
                props.className?.arrows,
                props.className?.left_arrow,
              )}
              disabled={!props.enableLoop && selectedIndex === 0}
              onClick={() => {
                onPrev();
                scrollThumbs(-56);
                scrollDots(-32);
              }}
            >
              {props.customArrowIcons?.left ?? <LuMoveLeft className="h-4 w-4" />}
            </button>
            <button
              className={cn(
                'absolute right-2 top-[calc(50%-8px)] rounded-md bg-gray-100 bg-opacity-80 p-1 text-foreground disabled:opacity-50',
                'ea-carousel__control ea-carousel__control--right',
                props.className?.arrows,
                props.className?.right_arrow,
              )}
              disabled={!props.enableLoop && selectedIndex === props.slides.length - 1}
              onClick={() => {
                onNext();
                scrollThumbs(56);
                scrollDots(32);
              }}
            >
              {props.customArrowIcons?.right ?? <LuMoveRight className="h-4 w-4" />}
            </button>
          </>
        )}
        {props.showDots && (
          <div
            className={cn(
              'absolute bottom-4 flex w-full items-center justify-center',
              'ea-carousel__dots__container',
              props.className?.dots,
            )}
          >
            <div
              className={cn(
                'flex max-w-[160px] cursor-pointer justify-start overflow-hidden',
                'ea-carousel__dots__wrapper',
              )}
              ref={dotsRef}
            >
              {slides.map((_, index) => (
                <LuDot
                  key={index}
                  className={cn(
                    `h-10 w-10 ${
                      index === selectedIndex ? 'text-foreground' : 'text-muted-foreground'
                    }`,
                    'ea-carousel__dot',
                    props.className?.dot,
                  )}
                  onClick={() => {
                    if (emblaApi) {
                      emblaApi.scrollTo(index);
                      ('Dot clicked, scrolled to index:', index);
                    }
                  }}
                />
              ))}
            </div>
          </div>
        )}
        {props.customDots?.showCustomDots && (
          <div
            className={cn(
              'flex w-full items-center justify-center',
              'ea-carousel__custom-dots__container',
              props.className?.customDots,
            )}
          >
            <div
              className={cn('flex w-full cursor-pointer', 'ea-carousel__custom-dots__wrapper')}
              ref={dotsRef}
            >
              {props.customDots.customDots.map((dot, index) => (
                <div
                  key={index}
                  className={cn(
                    `h-full w-full ${
                      index === selectedIndex
                        ? props.customDots?.colors.activeColor
                        : props.customDots?.colors.inactiveColor
                    }`,
                    props.className?.customDot,
                    'ea-carousel__custom-dot',
                  )}
                  onClick={() => {
                    if (emblaApi) {
                      emblaApi.scrollTo(index);
                      ('Custom dot clicked, scrolled to index:', index);
                    }
                  }}
                >
                  <div
                    className={cn(
                      'absolute left-1/2 top-[-8px] border-solid',
                      'ea-carousel__custom-dot-extra',
                    )}
                    style={{
                      content: '""',
                      transform: 'translateX(-50%)',
                      borderWidth: '0 5px 8px 5px',
                      borderColor: `transparent transparent ${
                        index === selectedIndex ? 'hsl(var(--accent))' : 'hsl(var(--secondary))'
                      } transparent`,
                    }}
                  />
                  {dot}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
